import { cleanName, cleanAddress, parseAge, parseWeight, digitsOnly } from './parsers';

/** Normalizes a spoken email ("john at gmail dot com") into a real address. */
export function parseEmail(text) {
  return String(text)
    .toLowerCase()
    .replace(/\s+at\s+/g, '@')
    .replace(/\s+dot\s+/g, '.')
    .replace(/\s+underscore\s+/g, '_')
    .replace(/\s+/g, '')
    .trim();
}

/**
 * GUIDED_STEPS — declarative description of every field the guided voice flow
 * walks through. The GuidedVoiceIntake component resolves each step against
 * the live form option data and writes the accepted value through the form's
 * own handlers (see GuidedVoiceIntake for the per-kind commit logic).
 *
 * kind:
 *   'text'   — free-text input written via native input event (uses parser)
 *   'number' — numeric input (uses parser; digits/measurements)
 *   'select' — <select> written via native change event; matched with matchOption
 *   'combo'  — custom country/state/city search-combo; committed via onCombo
 *   'dob'    — antd DatePicker; committed via onSetDob(moment)
 *
 * For 'select': optionsKey indexes optionData; optType 'object' → {id,name}
 *   (label=name, value=id); optType 'string' → plain strings with a disabled
 *   placeholder at index 0 (skipped).
 * For 'combo': comboKind identifies which handler set to use; optionsKey is the
 *   live filtered list.
 *
 * MANDATORY fields (no `optional` flag) are enforced by the guided flow: their
 * Skip button is hidden and Accept is blocked until a valid value is present.
 * The required set mirrors validateGeneralInfo() in Case.js/Dashboard.js.
 * `minDigits` additionally requires an exact digit count before Accept, matching
 * the form's own length checks (phone/emergency = 10, pin = 6).
 */
export const GUIDED_STEPS = [
  { id: 'firstName', name: 'firstName', label: 'First Name', kind: 'text', parser: cleanName,
    question: 'first name', example: 'say "Rahul", "Priya"' },
  { id: 'middleName', name: 'middleName', label: 'Middle Name', kind: 'text', parser: cleanName,
    optional: true, question: 'middle name', example: 'say a middle name, or Skip' },
  { id: 'lastName', name: 'lastName', label: 'Last Name', kind: 'text', parser: cleanName,
    question: 'last name', example: 'say "Sharma", "Kumar"' },
  { id: 'email', name: 'email', label: 'Email Address', kind: 'text', parser: parseEmail,
    optional: true, question: 'email address', example: 'say "rahul at gmail dot com"' },

  { id: 'dob', name: 'dob', label: 'Date of Birth', kind: 'dob',
    question: 'date of birth', example: 'say "5 March 1990" or "1990-03-05"' },

  { id: 'gender', name: 'gender', label: 'Gender', kind: 'select', optionsKey: 'genderArr',
    optType: 'object', question: 'gender', example: 'say "male", "female"' },
  { id: 'bloodGroup', name: 'bloodGroup', label: 'Blood Group', kind: 'select', optionsKey: 'bloodGroupArr',
    optType: 'string', question: 'blood group', example: 'say "O positive", "AB negative"' },

  { id: 'religion', name: 'religion', label: 'Religion', kind: 'select', optionsKey: 'religionArr',
    optType: 'string', optional: true, question: 'religion', example: 'say a religion, or Skip' },
  { id: 'education', name: 'education', label: 'Education', kind: 'select', optionsKey: 'educationArr',
    optType: 'string', optional: true, question: 'education', example: 'say an education level, or Skip' },
  { id: 'occupation', name: 'occupation', label: 'Occupation', kind: 'select', optionsKey: 'occupationArr',
    optType: 'string', optional: true, question: 'occupation', example: 'say an occupation, or Skip' },
  { id: 'maritalStatus', name: 'maritalStatus', label: 'Marital Status', kind: 'select', optionsKey: 'maritalStatusArr',
    optType: 'string', optional: true, question: 'marital status', example: 'say "single", "married"' },

  { id: 'idProofType', name: 'idProofType', label: 'ID Proof Type', kind: 'select', optionsKey: 'idProofTypeArr',
    optType: 'string', optional: true, question: 'ID proof type', example: 'say "Aadhaar", "Passport"' },
  { id: 'idProofNumber', name: 'idProofNumber', label: 'ID Proof Number', kind: 'text', parser: (t) => String(t).replace(/\s+/g, '').toUpperCase(),
    optional: true, question: 'ID proof number', example: 'say the ID number, or Skip' },

  { id: 'emergencyContactPerson', name: 'emergencyContactPerson', label: 'Emergency Contact Person', kind: 'text', parser: cleanName,
    question: 'emergency contact person', example: 'say a name' },
  { id: 'emergencyContactRelation', name: 'emergencyContactRelation', label: 'Relation', kind: 'select', optionsKey: 'relationArr',
    optType: 'string', question: 'relation of emergency contact', example: 'say "father", "spouse"' },
  { id: 'emergencyMobileNumber', name: 'emergencyMobileNumber', label: 'Emergency Contact Number', kind: 'number', parser: digitsOnly, minDigits: 10,
    question: 'emergency contact number', example: 'say 10 digits' },

  { id: 'phoneNumber1', name: 'phoneNumber1', label: 'Phone Number', kind: 'number', parser: digitsOnly, minDigits: 10,
    question: 'phone number', example: 'say 10 digits' },

  { id: 'addressline1', name: 'addressline1', label: 'Address Line 1', kind: 'text', parser: cleanAddress, long: true,
    question: 'permanent address line 1', example: 'say the address' },
  { id: 'addressline2', name: 'addressline2', label: 'Address Line 2', kind: 'text', parser: cleanAddress, long: true,
    optional: true, question: 'permanent address line 2', example: 'say more of the address, or Skip' },

  { id: 'country', name: 'country', label: 'Country', kind: 'combo', comboKind: 'country', optionsKey: 'filteredCountryList',
    question: 'country', example: 'say "India"' },
  { id: 'state', name: 'state', label: 'State/Province', kind: 'combo', comboKind: 'state', optionsKey: 'filteredStateList',
    question: 'state or province', example: 'say the state name' },
  { id: 'city', name: 'city', label: 'City', kind: 'combo', comboKind: 'city', optionsKey: 'filteredCityList',
    question: 'city', example: 'say the city name' },
  { id: 'pinCode', name: 'pinCode', label: 'Pin Code', kind: 'number', parser: digitsOnly, minDigits: 6,
    question: 'pin code', example: 'say 6 digits' },

  { id: 'phoneCountryCode', name: 'country', label: 'Phone Country Code', kind: 'select', optionsKey: 'countryArr',
    optType: 'object', question: 'phone country code', example: 'say "India"' },
  { id: 'primaryConsultant', name: 'primaryConsultant', label: 'Primary Consultant', kind: 'text', parser: cleanName,
    question: 'primary consultant', example: 'say the consultant name' },
  { id: 'institute', name: 'institute', label: 'Institute', kind: 'select', optionsKey: 'instituteArr',
    optType: 'object', question: 'institute', example: 'say the institute name' },
  { id: 'patientType', name: 'patientType', label: 'Patient Type', kind: 'select', optionsKey: 'patientTypeArr',
    optType: 'string', question: 'patient type', example: 'say the patient type' },
];

/** Returns {options, getLabel, getValue} for a select/combo step given live data. */
export function resolveOptions(step, optionData) {
  const raw = (optionData && optionData[step.optionsKey]) || [];
  if (step.kind === 'combo') {
    // Country/state/city lists are always {id, name} objects.
    return {
      options: raw,
      getLabel: (o) => (o && o.name != null ? o.name : String(o)),
      getValue: (o) => o, // combo commits the whole item via onCombo
    };
  }
  if (step.optType === 'object') {
    return {
      options: raw,
      getLabel: (o) => (o && o.name != null ? o.name : String(o)),
      getValue: (o) => (o && o.id != null ? o.id : o),
    };
  }
  // string array: skip the placeholder at index 0
  return {
    options: raw.slice(1),
    getLabel: (o) => String(o),
    getValue: (o) => o,
  };
}
